import "@/core/styles/globals.css";
import React, { useEffect } from "react";
import PropTypes from "prop-types";
import Head from "next/head";
import type { AppProps } from "next/app";
import CssBaseline from "@mui/material/CssBaseline";
import { ClaimCheckProvider } from "@/core/contexts/ClaimCheckContext/ClaimCheckContext";
import DynamicThemeProviders from "@/core/components/Theme";
import Layout from "@/core/components/Layout/index";
import { ErrorBoundary } from "@/core/components/ErrorBoundary/ErrorBoundary";
import { init as initDataDog } from "@/core/lib/datadog/datadog";
import { TranslationsProvider } from "@/core/contexts/TranslationsContext";
import { ScriptLoader } from "@/core/components/ScriptLoader/ScriptLoader";
import { ConfigProvider } from "@/core/contexts/ConfigContext";
import { Brand, Domains, LegalArea } from "@/core/types/types";
import { CacheProvider, EmotionCache } from "@emotion/react";
import createEmotionCache from "@/core/utils/createEmotionCache";
import {
  pushCCStartedWebhook,
  trackCCStartedEvent,
} from "@/core/utils/trackPageViewEvents";
import { getDomainData } from "@/core/utils/getBrandConfiguration";

interface ClientAppProps extends AppProps {
  emotionCache: EmotionCache;
}

const clientSideEmotionCache: EmotionCache = createEmotionCache();

const PARAMETERS_TO_REMOVE: string[] = [
  "processinstanceid",
  "flightProblem",
  "action",
  "processDefinition",
  "caseDataToken",
  "syncSignature",
];

const App = function ({
  Component,
  pageProps,
  emotionCache = clientSideEmotionCache,
}: ClientAppProps) {
  const { translations, initialisationConfig } = pageProps;
  const {
    brandId = Brand.DEFAULT,
    domain: tld = Domains.NET,
    query: queryParams,
    definitionName,
    promotionCode = "",
    legalArea,
  }: {
    brandId: Brand;
    domain: Domains;
    query?: any;
    definitionName: string;
    promotionCode?: string;
    legalArea: LegalArea;
    isNewInstance?: boolean;
  } = initialisationConfig || {};
  const domain: string = `${brandId}.${tld}`;
  const DynamicThemeProvider: any =
    DynamicThemeProviders[brandId] || DynamicThemeProviders[Brand.DEFAULT];
  const href: string = `${brandId}.ico`;
  const brandFamily: string | undefined = initialisationConfig?.brandFamily;
  const brandCanonicalUrl = brandFamily
    ? getDomainData(brandFamily, legalArea, tld).CanonicalUrl?.url
    : getDomainData(Brand.FLIGHTRIGHT, LegalArea.DEFAULT, Domains.DE)
        .CanonicalUrl?.url;

  useEffect(() => {
    if (initialisationConfig.isNewInstance) {
      trackCCStartedEvent(initialisationConfig.flightProblem);
      pushCCStartedWebhook(initialisationConfig.flightProblem);
      initialisationConfig.isNewInstance = false;
    }
  }, []);

  useEffect(() => {
    if (queryParams) {
      const urlSearchParams = new URLSearchParams(window.location.search);

      for (const key in queryParams) {
        if (queryParams.hasOwnProperty(key)) {
          urlSearchParams.set(key, queryParams[key]);
        }
      }
      for (const param of PARAMETERS_TO_REMOVE) {
        urlSearchParams.delete(param);
      }
      const newSearch = urlSearchParams.toString();
      const newURL = newSearch
        ? `${window.location.pathname}?${newSearch}`
        : window.location.pathname;

      window.history.replaceState({}, document.title, newURL);
    }
  }, [queryParams]);

  useEffect(() => {
    initDataDog({
      environment: process.env.NEXT_PUBLIC_DEPLOYMENT_ENV || "development",
      version: process.env.NEXT_PUBLIC_APP_VERSION || "development",
      legalArea,
      processInstanceId: initialisationConfig.processId,
      definitionName: initialisationConfig.definitionName,
    });
  }, []);

  const title: string = translations?.["opengraph.title"];
  return (
    <>
      <Head>
        <title>{title}</title>
        <meta
          name="viewport"
          content="minimum-scale=1, initial-scale=1, width=device-width"
        />
        <link rel="icon" href={href} />
        <link rel="canonical" href={brandCanonicalUrl} />
      </Head>
      <ConfigProvider config={initialisationConfig}>
        <CacheProvider value={emotionCache}>
          <DynamicThemeProvider>
            <TranslationsProvider initialTranslations={translations}>
              <Layout brandName={brandId} promoCode={promotionCode}>
                <CssBaseline />
                <ClaimCheckProvider>
                  <ErrorBoundary
                    definitionName={definitionName}
                    domain={domain}
                  >
                    <Component {...pageProps} />
                  </ErrorBoundary>
                </ClaimCheckProvider>
              </Layout>
            </TranslationsProvider>
          </DynamicThemeProvider>
        </CacheProvider>
      </ConfigProvider>

      <ScriptLoader brandName={brandId} tld={tld} />
    </>
  );
};

App.propTypes = {
  Component: PropTypes.elementType.isRequired,
  pageProps: PropTypes.object.isRequired,
};

export default App;
